import React, { useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { TeamsPage } from "./pages/TeamsPage";
import { LeaguePage } from "./pages/LeaguePage";
import { MatchPage } from "./pages/MatchPage";
import { PlayNowPage } from "./pages/PlayNowPage";
import { SaveLoadPage } from "./pages/SaveLoadPage";
import { StatsPage } from "./pages/StatsPage";
import { CupPage } from "./pages/CupPage";
import { ManagerPage } from "./pages/ManagerPage";
import { AsianCupPage } from "./pages/AsianCupPage";
import { ClubWorldCupPage } from "./pages/ClubWorldCupPage";
import { WorldCupPage } from "./pages/WorldCupPage";
import { PlayerCareerPage } from "./pages/PlayerCareerPage";
import { PlayerCareerSelectPage } from "./pages/PlayerCareerSelectPage";
import { TrophyHistoryPage } from "./pages/TrophyHistoryPage";
import { BlueLockPage } from "./pages/BlueLockPage";
import { CreditsPage } from "./pages/CreditsPage";
import { createInitialGameState, saveGame, loadGame, setSaveScope, deleteSaveSlot } from "./utils/gameData";
import { IntroSplash, shouldShowIntro } from "./components/IntroSplash";

const EMPTY_LEAGUE = { teams: [], fixtures: [], currentWeek: 1, season: 1, mode: 'league' };
const EMPTY_MANAGER = { teams: [], fixtures: [], currentWeek: 1, season: 1, mode: 'manager', managerMode: null };
const EMPTY_CAREER = null;

function AppContent() {
  const { user, loading, logout } = useAuth();

  const [leagueState, setLeagueState] = useState(EMPTY_LEAGUE);
  const [managerState, setManagerState] = useState(EMPTY_MANAGER);
  const [careerState, setCareerState] = useState(EMPTY_CAREER);
  const [hydrated, setHydrated] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setSaveScope('anon');
      setLeagueState(EMPTY_LEAGUE);
      setManagerState(EMPTY_MANAGER);
      setCareerState(EMPTY_CAREER);
      setHydrated(false);
      return;
    }
    setSaveScope(user.id);
    const savedLeague = loadGame('league_autosave');
    const savedManager = loadGame('manager_autosave');
    const savedCareer = loadGame('player_career_autosave');
    setLeagueState(savedLeague || EMPTY_LEAGUE);
    setManagerState(savedManager || EMPTY_MANAGER);
    setCareerState(savedCareer || EMPTY_CAREER);
    setHydrated(true);
  }, [user, loading]);

  useEffect(() => {
    if (!hydrated || !user) return;
    if (leagueState.teams?.length > 0) saveGame(leagueState, 'league_autosave');
  }, [leagueState, hydrated, user]);

  useEffect(() => {
    if (!hydrated || !user) return;
    if (managerState.teams?.length > 0) saveGame(managerState, 'manager_autosave');
  }, [managerState, hydrated, user]);

  useEffect(() => {
    if (!hydrated || !user) return;
    if (careerState?.teams?.length > 0) saveGame(careerState, 'player_career_autosave');
  }, [careerState, hydrated, user]);

  const handleNewLeagueGame = useCallback(() => {
    const ns = createInitialGameState();
    ns.mode = 'league';
    setLeagueState(ns);
    return ns;
  }, []);

  const handleResetLeague = useCallback(() => {
    deleteSaveSlot('league_autosave');
    setLeagueState(EMPTY_LEAGUE);
  }, []);

  const handleResetManager = useCallback(() => {
    deleteSaveSlot('manager_autosave');
    setManagerState(EMPTY_MANAGER);
  }, []);

  const handleResetCareer = useCallback(() => {
    deleteSaveSlot('player_career_autosave');
    setCareerState(EMPTY_CAREER);
  }, []);

  const ensureLeague = useCallback(() => {
    if (!leagueState?.teams?.length) return handleNewLeagueGame();
    return leagueState;
  }, [leagueState, handleNewLeagueGame]);

  const handleUpdateLeague = useCallback((ns) => {
    if (typeof ns === 'function') setLeagueState(prev => ns(prev));
    else setLeagueState(ns);
  }, []);
  const handleUpdateManager = useCallback((ns) => {
    if (typeof ns === 'function') setManagerState(prev => ns(prev));
    else setManagerState(ns);
  }, []);
  const handleUpdateCareer = useCallback((ns) => {
    if (typeof ns === 'function') setCareerState(prev => ns(prev));
    else setCareerState(ns);
  }, []);
  const handleUpdateLeagueTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, teams: t })), []);
  const handleUpdateChampionsLeagueTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, championsLeagueTeams: t })), []);
  const handleUpdateEuropaLeagueTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, europaLeagueTeams: t })), []);
  const handleUpdateConferenceLeagueTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, conferenceLeagueTeams: t })), []);
  const handleUpdateCwcTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, cwcTeams: t })), []);
  const handleUpdateWcTeams = useCallback((t) => setLeagueState(prev => ({ ...prev, wcTeams: t })), []);
  const handleUpdateLeagueTeam = useCallback((teamId, updates) => {
    setLeagueState(prev => ({ ...prev, teams: prev.teams.map(t => t.id === teamId ? { ...t, ...updates } : t) }));
  }, []);
  const handleLoadGame = useCallback((loaded) => {
    if (loaded.mode === 'manager') setManagerState(loaded);
    else if (loaded.mode === 'player_career') setCareerState(loaded);
    else setLeagueState(loaded);
  }, []);

  const handleLogout = useCallback(async () => {
    // Flush in-flight states to localStorage under the CURRENT user scope
    // before logout — prevents any race where the anon scope kicks in first.
    try {
      if (leagueState?.teams?.length > 0) saveGame(leagueState, 'league_autosave');
      if (managerState?.teams?.length > 0) saveGame(managerState, 'manager_autosave');
      if (careerState?.teams?.length > 0) saveGame(careerState, 'player_career_autosave');
    } catch {}
    await logout();
    setLeagueState(EMPTY_LEAGUE);
    setManagerState(EMPTY_MANAGER);
    setCareerState(EMPTY_CAREER);
    setSaveScope('anon');
    setHydrated(false);
  }, [logout, leagueState, managerState, careerState]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04060F] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#00E5FF] rounded-md mx-auto flex items-center justify-center mb-4 animate-pulse"
            style={{ boxShadow: '0 0 28px rgba(0,229,255,0.55)' }}>
            <span className="font-heading font-black text-[#04060F] text-2xl">JW</span>
          </div>
          <p className="text-[#7A86B5] text-sm tracking-[0.18em] uppercase">Loading JWFL · 日本</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        {showIntro && <IntroSplash onDone={() => setShowIntro(false)} />}
        <LoginPage />
      </>
    );
  }

  const hasLeague = leagueState?.teams?.length > 0;
  const hasCareer = !!careerState?.playerId;

  return (
    <>
      {showIntro && <IntroSplash onDone={() => setShowIntro(false)} />}
      <Navigation user={user} onLogout={handleLogout} />
      <Routes>
        <Route path="/" element={<HomePage gameState={leagueState} managerState={managerState} careerState={careerState} onNewGame={handleNewLeagueGame} onResetLeague={handleResetLeague} onResetManager={handleResetManager} onResetCareer={handleResetCareer} />} />
        <Route path="/play-now" element={<PlayNowPage teams={hasLeague ? leagueState.teams : ensureLeague().teams} wcTeams={leagueState.wcTeams || []} onUpdateTeams={handleUpdateLeagueTeams} />} />
        <Route path="/teams" element={hasLeague ? <TeamsPage teams={leagueState.teams} championsLeagueTeams={leagueState.championsLeagueTeams || []} europaLeagueTeams={leagueState.europaLeagueTeams || []} conferenceLeagueTeams={leagueState.conferenceLeagueTeams || []} cwcTeams={leagueState.cwcTeams || []} wcTeams={leagueState.wcTeams || []} onUpdateTeam={handleUpdateLeagueTeam} onUpdateTeams={handleUpdateLeagueTeams} onUpdateChampionsLeagueTeams={handleUpdateChampionsLeagueTeams} onUpdateEuropaLeagueTeams={handleUpdateEuropaLeagueTeams} onUpdateConferenceLeagueTeams={handleUpdateConferenceLeagueTeams} onUpdateCwcTeams={handleUpdateCwcTeams} onUpdateWcTeams={handleUpdateWcTeams} /> : <Navigate to="/" replace />} />
        <Route path="/league" element={hasLeague ? <LeaguePage gameState={leagueState} onUpdateGameState={handleUpdateLeague} /> : <Navigate to="/" replace />} />
        <Route path="/match" element={hasLeague ? <MatchPage teams={leagueState.teams} onUpdateTeams={handleUpdateLeagueTeams} /> : <Navigate to="/" replace />} />
        <Route path="/stats" element={hasLeague ? <StatsPage teams={leagueState.teams} /> : <Navigate to="/" replace />} />
        <Route path="/cup" element={hasLeague ? <CupPage gameState={leagueState} onUpdateGameState={handleUpdateLeague} /> : <Navigate to="/" replace />} />
        <Route path="/asian-cup" element={hasLeague ? <AsianCupPage gameState={leagueState} onUpdateGameState={handleUpdateLeague} /> : <Navigate to="/" replace />} />
        {/* Legacy redirects — keep old UCL/UEL/UECL routes mapping to the Asian Cup */}
        <Route path="/champions-league" element={<Navigate to="/asian-cup" replace />} />
        <Route path="/europa-league" element={<Navigate to="/asian-cup" replace />} />
        <Route path="/conference-league" element={<Navigate to="/asian-cup" replace />} />
        <Route path="/club-world-cup" element={hasLeague ? <ClubWorldCupPage gameState={leagueState} onUpdateGameState={handleUpdateLeague} /> : <Navigate to="/" replace />} />
        <Route path="/world-cup" element={hasLeague ? <WorldCupPage gameState={leagueState} onUpdateGameState={handleUpdateLeague} /> : <Navigate to="/" replace />} />
        <Route path="/trophies" element={hasLeague ? <TrophyHistoryPage gameState={leagueState} /> : <Navigate to="/" replace />} />
        <Route path="/manager" element={<ManagerPage gameState={managerState.teams?.length ? managerState : (() => { const ns = createInitialGameState(); ns.mode = 'manager'; return ns; })()} onUpdateGameState={handleUpdateManager} onResetManager={handleResetManager} />} />
        <Route path="/player-career" element={hasCareer ? <PlayerCareerPage careerState={careerState} onUpdateCareer={handleUpdateCareer} onResetCareer={handleResetCareer} /> : <Navigate to="/player-career/select" replace />} />
        <Route path="/player-career/select" element={<PlayerCareerSelectPage onStartCareer={(ns) => { handleResetCareer(); setCareerState(ns); }} />} />
        <Route path="/blue-lock" element={<BlueLockPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/save" element={<SaveLoadPage gameState={leagueState} managerState={managerState} careerState={careerState} onLoadGame={handleLoadGame} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
